import { ComponentProvider } from '@atoms/ComponentProvider';
import { DialogDiagnosticProvider } from '@atoms/DiagnosticContext';
import { DEFAULT_PROJECT_BROWSER_WIDTH, useIDEParams, useIdeActions, useIdeState } from '@common/ide/state';
import { Entity } from '@common/types/Entity';
import { ViewType } from '@LSP/types';
import { theme } from '@prophecy/theme';
import { Stack, StackItem, Text } from '@prophecy/ui-v3';
import DocumentTitle from '@prophecy/utils/react/DocumentTitle';
import React, { ReactNode, useCallback } from 'react';

import { LogLineAndColumn } from '../../generic-components/Logger/LogLineAndCol';
import { Log } from '../../generic-components/Logger/types';
import { ComponentRunContext, useRunDataContext } from '../Workflow/AttachFabric/componentRunhook';
import { ConnectorObservations } from './Components/ConnectorObservations';
import { Footer } from './Components/Footer';
import { Header } from './Components/Header';
import { ProjectObservations } from './Components/ProjectObservations';

export const Container = () => {
  const viewType = useIdeState((state) => state.ide.viewType);

  const { observedEntity } = useIDEParams();

  const runData = useRunDataContext();

  const projectBrowserWidth = useIdeState((state) => state.projectBrowserWidth);
  const hasProjectBrowser = useIdeState((state) => state.hasProjectBrowser);
  const ideActions = useIdeActions();
  const toggleProjectBrowser = useCallback(
    (show: boolean) => {
      const width = projectBrowserWidth > 1 ? projectBrowserWidth : DEFAULT_PROJECT_BROWSER_WIDTH;
      ideActions.setProjectBrowserWidth(width, show);
    },
    [ideActions, projectBrowserWidth]
  );

  const setViewType = async (viewType: ViewType) => {
    ideActions.changeViewType(viewType);
  };

  const renderLogLabel = (log: Log): ReactNode => {
    return (
      <Text level='sm' weight={theme.fontWeight.medium}>
        <LogLineAndColumn log={log} />
      </Text>
    );
  };

  return (
    <DialogDiagnosticProvider propertyPath={[``]} isMetadata={true}>
      <ComponentRunContext.Provider value={runData}>
        <ComponentProvider>
          <Stack height='100%'>
            <StackItem grow='1'>
              <Stack height='100%'>
                <DocumentTitle title={`Data Quality Observations`} />
                <Header
                  viewType={viewType}
                  setViewType={setViewType}
                  showProjectBrowser={hasProjectBrowser}
                  toggleProjectBrowser={toggleProjectBrowser}
                />
                <StackItem grow='1'>
                  {observedEntity === Entity.Connector ? <ConnectorObservations /> : <ProjectObservations />}
                </StackItem>
              </Stack>
            </StackItem>
            <Footer renderLogLabel={renderLogLabel} />
          </Stack>
        </ComponentProvider>
      </ComponentRunContext.Provider>
    </DialogDiagnosticProvider>
  );
};
